<script setup lang="ts">
import { computed } from 'vue'

import type { InsightConfig } from '../../../models/insightConfig.types'
import { formatStructuredInsightValue } from '../../../service/insightValue'

import { useI18n } from '@/shared/i18n'
import { Accordion, type AccordionItemOption } from '@/shared/ui/accordion'

const props = defineProps<{ config: InsightConfig | null }>()
const { t } = useI18n()
const items = computed<AccordionItemOption[]>(() => [
  { id: 'identity', label: t('insights.sysadmin.config.sections.identity'), defaultOpen: true },
  { id: 'detection', label: t('insights.sysadmin.config.sections.detection') },
  { id: 'templates', label: t('insights.sysadmin.config.sections.templates') },
  { id: 'pipeline', label: t('insights.sysadmin.config.sections.pipeline') },
  { id: 'visualization', label: t('insights.sysadmin.config.sections.visualization') },
])

function section(id: string): unknown {
  if (!props.config) return null
  if (id === 'identity') {
    const { id: configId, enum: configEnum, label, status, priority, platform } = props.config
    return { id: configId, enum: configEnum, label, status, priority, platform }
  }
  if (id === 'detection') {
    return {
      detection_config: props.config.detection_config,
      detection_sources: props.config.detection_sources,
      eligibility_config: props.config.eligibility_config,
      lookback_days: props.config.lookback_days,
    }
  }
  if (id === 'templates') {
    return {
      title_template: props.config.title_template,
      content_template: props.config.content_template,
      template_variables: props.config.template_variables,
    }
  }
  if (id === 'pipeline') {
    return { pipeline_steps: props.config.pipeline_steps, llm_config: props.config.llm_config }
  }
  return {
    content_type: props.config.content_type,
    column_definitions: props.config.column_definitions,
    hidden_dimensions: props.config.hidden_dimensions,
    tab_definitions: props.config.tab_definitions,
    visualization_config: props.config.visualization_config,
  }
}
</script>

<template>
  <section class="config-inspector" data-testid="insight-config-inspector">
    <p v-if="!config">{{ t('insights.sysadmin.config.empty') }}</p>
    <Accordion v-else :items="items" multiple>
      <template #item="{ item }">
        <pre><code>{{ formatStructuredInsightValue(section(item.id)) }}</code></pre>
      </template>
    </Accordion>
  </section>
</template>

<style scoped>
.config-inspector pre {
  max-height: 26rem;
  margin: 0;
  overflow: auto;
  padding: var(--ui-space-sm);
  border-radius: var(--ui-radius-sm);
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-fg);
  font-family: var(--ui-font-numeric);
  white-space: pre-wrap;
}
.config-inspector p {
  margin: 0;
  padding: var(--ui-space-lg);
  color: var(--ui-color-muted);
  text-align: center;
}
</style>
